/**
 * NotificationPanel — In-app notification center
 *
 * Now backed by activity-feed.ts engine for rich activity types.
 * Links to /activity for full-page experience.
 *
 * @see src/lib/activity-feed.ts
 * @see Issue #110, #141
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  markAllRead,
  saveFeed,
  loadFeed,
  type ActivityItem,
  type ActivityType,
} from "@shared-lib/activity-feed";
import { ActivityIcon } from "@/components/ActivityIcon";

/* ── Type visual config ── */
const TYPE_CONFIG: Record<ActivityType, { iconType: string; color: string; label: string }> = {
  atom_published: { iconType: "publish", color: "bg-blue-50 text-blue-600", label: "发布" },
  atom_resonated: { iconType: "resonance", color: "bg-amber-50 text-amber-600", label: "共鸣" },
  discussion_reply: { iconType: "discuss", color: "bg-indigo-50 text-indigo-600", label: "回复" },
  topic_new_atom: { iconType: "link", color: "bg-green-50 text-green-600", label: "话题" },
  follow_new_atom: { iconType: "follow", color: "bg-cyan-50 text-cyan-600", label: "关注" },
  weekly_review: { iconType: "review", color: "bg-purple-50 text-purple-600", label: "回顾" },
  level_up: { iconType: "upgrade", color: "bg-yellow-50 text-yellow-700", label: "升级" },
  daily_collision: { iconType: "sparkle", color: "bg-pink-50 text-pink-600", label: "碰撞" },
  badge_earned: { iconType: "badge", color: "bg-orange-50 text-orange-600", label: "徽章" },
};

/* ── Relative time formatter ── */
function relativeTime(isoStr: string): string {
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "刚刚";
  if (mins < 60) return `${mins} 分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} 天前`;
  return new Date(isoStr).toLocaleDateString("zh-CN");
}

/* ── Bell icon with badge ── */
export function NotificationBell({
  onClick,
  unreadCount,
}: {
  onClick: () => void;
  unreadCount: number;
}) {
  return (
    <button
      onClick={onClick}
      className="relative text-[var(--ig-text-2)] hover:text-[var(--ig-accent)] transition flex items-center"
      aria-label="通知"
      title="通知"
    >
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.8}
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"
        />
      </svg>
      {unreadCount > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full px-1">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  );
}

/* ── Notification Panel ── */
export function NotificationPanel({
  open,
  onClose,
  realNotifications,
  onMarkAllRead,
}: {
  open: boolean;
  onClose: () => void;
  realNotifications?: Array<{ id: string; type: string; actor_id: string | null; target_type: string | null; target_id: string | null; read: boolean; created_at: string }>;
  onMarkAllRead?: () => Promise<void>;
}) {
  const [items, setItems] = useState<ActivityItem[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  // Build display items from real notifications or mock fallback
  useEffect(() => {
    if (!open) return;

    if (realNotifications && realNotifications.length > 0) {
      // Map real Supabase notifications → ActivityItem format for display
      const mapped: ActivityItem[] = realNotifications.slice(0, 8).map(n => {
        const typeMap: Record<string, ActivityType> = {
          follow: 'follow_new_atom',
          reaction: 'atom_resonated',
          comment: 'discussion_reply',
          repost: 'atom_published',
          mention: 'discussion_reply',
          system: 'weekly_review',
        };
        const labelMap: Record<string, string> = {
          follow: '有人关注了你',
          reaction: '你的内容收到了共鸣',
          comment: '有人回复了你',
          repost: '你的内容被转发',
          mention: '有人提到了你',
          system: '系统通知',
        };
        const categoryMap: Record<string, 'content' | 'social' | 'system' | 'achievement'> = {
          follow: 'social',
          reaction: 'social',
          comment: 'social',
          repost: 'content',
          mention: 'social',
          system: 'system',
        };
        return {
          id: n.id,
          type: typeMap[n.type] ?? 'daily_collision',
          title: labelMap[n.type] ?? '通知',
          description: n.target_type ? `来自 ${n.target_type}` : '',
          actorId: n.actor_id,
          actorName: null,
          targetId: n.target_id,
          targetType: (n.target_type as 'atom' | 'topic' | 'discussion' | 'user') ?? null,
          timestamp: n.created_at,
          read: n.read,
          actionUrl: null,
          category: categoryMap[n.type] ?? 'system',
        };
      });
      setItems(mapped);
    } else {
      // No real notifications yet — show empty
      setItems([]);
    }
  }, [open, realNotifications]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, onClose]);

  // Mark all as read
  const handleMarkAllRead = useCallback(() => {
    if (onMarkAllRead) {
      onMarkAllRead();
    } else {
      // Fallback: mock mode local feed
      const saved = loadFeed();
      if (saved) {
        const updated = markAllRead(saved);
        saveFeed(updated);
        setItems(updated.items.slice(0, 6));
      }
    }
  }, [onMarkAllRead]);

  if (!open) return null;

  const unread = items.filter((n) => !n.read);

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-[var(--ig-surface)] rounded-xl shadow-2xl border border-slate-200/80 overflow-hidden z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h3 className="text-sm font-semibold text-slate-800">通知</h3>
        {unread.length > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            全部已读
          </button>
        )}
      </div>

      {/* List */}
      <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-50">
        {items.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">
            暂无通知
          </div>
        ) : (
          items.map((n) => {
            const config = TYPE_CONFIG[n.type] ?? { icon: "sparkle", color: "bg-slate-50 text-slate-600", label: "通知" };
            return (
              <Link
                key={n.id}
                to={n.actionUrl ?? "#"}
                onClick={onClose}
                className={`flex items-start gap-3 px-4 py-3 transition-colors hover:bg-slate-50 ${
                  !n.read ? "bg-blue-50/30" : ""
                }`}
              >
                <span
                  className={`mt-1 text-sm w-7 h-7 flex items-center justify-center rounded-full shrink-0 ${config.color}`}
                >
                  <ActivityIcon type={config.iconType} className="w-3.5 h-3.5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-700 truncate">
                      {n.title}
                    </span>
                    {!n.read && (
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mt-1">
                    {n.description}
                  </p>
                  <span className="text-xs text-slate-400 mt-1 block">
                    {relativeTime(n.timestamp)}
                  </span>
                </div>
              </Link>
            );
          })
        )}
      </div>

      {/* Footer — link to full activity page */}
      <div className="px-4 py-2 border-t border-slate-100 text-center">
        <Link
          to="/activity"
          onClick={onClose}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          查看全部动态
        </Link>
      </div>
    </div>
  );
}
